'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';

import { RatingStarIcon, StarRating } from '@/widgets/StarRating';

import { HERO_HIGHLIGHTS } from '@/constants/downloadApp';

import classes from '../../index.module.css';
import { useParallax } from '../hooks';
import PhoneMockup from '../PhoneMockup';
import StoreButtons from '../StoreButtons';

import { TickCircle } from 'iconsax-react';

const Hero = () => {
    const orbBack = useParallax<HTMLDivElement>(0.12);
    const orbMid = useParallax<HTMLDivElement>(0.06);

    return (
        <header
            className='relative overflow-hidden'
            style={{
                background:
                    'radial-gradient(1200px 600px at 85% -10%, rgba(0,143,255,0.35), transparent 60%), radial-gradient(900px 500px at 0% 110%, rgba(35,65,223,0.45), transparent 60%), linear-gradient(180deg, #050b26 0%, #0a1540 55%, #040a1f 100%)'
            }}>
            <div ref={orbBack} className='pointer-events-none absolute inset-0' aria-hidden>
                <span className={`${classes.orb} ${classes.orbSecondary} ${classes.orbDrift} -left-40 top-1/3 h-[480px] w-[480px]`} />
            </div>
            <div ref={orbMid} className='pointer-events-none absolute inset-0' aria-hidden>
                <span className={`${classes.orb} ${classes.orbPrimary} -right-32 -top-24 h-[420px] w-[420px]`} />
                <span className={`${classes.orb} ${classes.orbGold} bottom-10 right-1/4 h-64 w-64`} />
            </div>

            <nav className='desktop-width relative z-10 flex items-center justify-between px-4 pt-6 md:px-8' aria-label='ناوبری صفحه دانلود اپ'>
                <Link href='/' aria-label='صفحه اصلی ازکی‌سرویس' className='flex cursor-pointer items-center gap-2'>
                    <Image src='/images/logo/azkiservice-logo-white.svg' alt='لوگوی ازکی‌سرویس' width={132} height={36} priority />
                </Link>
                <a
                    href='#download'
                    className={`${classes.glassDark} hidden items-center gap-2 rounded-lg px-4 py-2 text-xs text-white transition hover:bg-white/15
                        md:flex`}>
                    <StarRating rating={4.6} size='sm' />
                    <span className='font-bold'>۴.۶</span>
                    <span className='text-whiteMediumEmphasis'>دانلود رایگان اپ</span>
                </a>
            </nav>

            <div className='desktop-width relative z-10 grid items-center gap-12 px-4 pb-20 pt-12 md:grid-cols-2 md:px-8 md:pb-28 md:pt-16'>
                <div className='flex flex-col items-center text-center md:items-start md:text-right'>
                    <span
                        className={`${classes.heroIn} ${classes.glassDark} mb-5 inline-flex items-center gap-2 rounded-lg px-4 py-1.5 text-xs
                            font-bold text-white`}
                        style={{ '--reveal-delay': '0ms' } as React.CSSProperties}>
                        <span className='h-2 w-2 animate-pulse rounded-full bg-success' aria-hidden />
                        دانلود اپلیکیشن رسمی ازکی‌سرویس
                    </span>

                    <h1
                        className={`${classes.heroIn} text-3xl font-black leading-[1.35] text-white md:text-5xl md:leading-[1.3]`}
                        style={{ '--reveal-delay': '90ms' } as React.CSSProperties}>
                        دانلود اپلیکیشن ازکی‌سرویس
                        <br />
                        <span className='text-primary'>خدمات خودرو و موتور، در محل شما</span>
                    </h1>

                    <p
                        className={`${classes.heroIn} mt-5 max-w-lg text-sm leading-8 text-whiteMediumEmphasis md:text-base md:leading-9`}
                        style={{ '--reveal-delay': '180ms' } as React.CSSProperties}>
                        با نصب رایگان اپ ازکی‌سرویس، تعویض باتری و روغن، کارشناسی خودرو و موتور، استعلام خلافی و پرداخت اقساطی را سریع و بدون مراجعه
                        حضوری انجام دهید.
                    </p>

                    <ul className='m-0 mt-6 flex w-full list-none flex-col gap-2.5 p-0 sm:flex-row sm:flex-wrap sm:justify-center md:justify-start'>
                        {HERO_HIGHLIGHTS.map((item, idx) => (
                            <li
                                key={item.id}
                                className={`${classes.heroIn} flex items-center justify-center gap-2 text-xs text-white md:justify-start md:text-sm`}
                                style={{ '--reveal-delay': `${260 + idx * 70}ms` } as React.CSSProperties}>
                                <TickCircle size={18} variant='Bold' color='#27AE60' aria-hidden />
                                {item.text}
                            </li>
                        ))}
                    </ul>

                    <div className={`${classes.heroIn} mt-8`} style={{ '--reveal-delay': '480ms' } as React.CSSProperties}>
                        <StoreButtons theme='dark' />
                    </div>
                </div>

                <div className={`${classes.heroIn} relative mx-auto w-fit`} style={{ '--reveal-delay': '300ms' } as React.CSSProperties}>
                    <div className={classes.float}>
                        <PhoneMockup
                            src='/images/screenshots/homePage.png'
                            alt='نمای صفحه اصلی اپلیکیشن ازکی‌سرویس روی موبایل'
                            priority
                            glow
                            className='w-[240px] md:w-[300px]'
                        />
                    </div>

                    <div
                        className={`${classes.glassDark} ${classes.floatSlow} absolute -right-8 top-16 z-10 hidden items-center gap-2.5 rounded-lg
                            px-4 py-3 sm:flex md:-right-16`}>
                        <span className='flex h-9 w-9 items-center justify-center rounded-lg bg-success/20'>
                            <TickCircle size={20} variant='Bold' color='#27AE60' aria-hidden />
                        </span>
                        <span className='text-right leading-tight'>
                            <span className='block text-xs font-bold text-white'>سفارش ثبت شد</span>
                            <span className='block text-[11px] text-whiteMediumEmphasis'>کارشناس در راه است</span>
                        </span>
                    </div>
                    <div
                        className={`${classes.glassDark} ${classes.float} absolute -left-6 bottom-24 z-10 hidden items-center gap-2.5 rounded-lg px-4
                            py-3 sm:flex md:-left-14`}>
                        <span className='flex h-9 w-9 items-center justify-center rounded-lg bg-primary/25'>
                            <RatingStarIcon size='md' />
                        </span>
                        <span className='text-right leading-tight'>
                            <span className='block text-xs font-bold text-white'>۴.۶ از ۵</span>
                            <span className='block text-[11px] text-whiteMediumEmphasis'>رضایت کاربران</span>
                        </span>
                    </div>
                </div>
            </div>

            <div
                className='pointer-events-none absolute inset-x-0 bottom-0 h-24'
                style={{ background: 'linear-gradient(180deg, transparent, rgba(4,10,31,0.9))' }}
                aria-hidden
            />
        </header>
    );
};

export default Hero;
